import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  DefaultPage,
  FieldType,
  FilterEvent,
  FilterName,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { TerminalsService } from "src/app/shared/services/terminals/terminals.service";

const USAGE_CODE = "SELECT_GUIDE";

@Component({
  selector: "app-select-guide-dialog",
  templateUrl: "./select-guide-dialog.component.html",
  styleUrls: ["./select-guide-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SelectGuideDialogComponent implements OnInit, OnDestroy {
  isLoading$ = new BehaviorSubject<boolean>(false);
  private loadingCounter = 0;
  totalGuides$ = new BehaviorSubject<number>(0);

  public guidesList: any[];

  private guidesSubscription: Subscription;

  public selectedRow: Object[] = [];

  public columnInfo$: Observable<ColumnSorterInfo[]>;
  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Guide.name",
      field: "name",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Guide.token",
      field: "token",
      fieldType: FieldType.STRING,
      searchable: true,
      hidden: true,
      identifier: true,
    },
    {
      header: "Guide.terminalType",
      field: "inferredTerminalType",
      fieldType: FieldType.STRING,
    },
    {
      header: "Guide.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATETIME,
      hidden: false,
    },
    {
      header: "Guide.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATETIME,
      hidden: false,
    },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private terminalsService: TerminalsService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private tableConfigService: TableConfigService,
    private dialogRef: MatDialogRef<SelectGuideDialogComponent>
  ) {}

  ngOnInit(): void {
    this.columnInfo$ = this.getTableConfig();
    this.loadTableData();
  }

  loadTableData(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const filter: FilterEvent = !this.data?.terminalType
      ? { advancedFilter: [] }
      : {
          advancedFilter: [
            {
              column: "inferredTerminalType",
              fieldType: FieldType.STRING,
              operator: FilterName.EQUALS,
              value: this.data.terminalType,
            },
          ],
        };

    filter.advancedFilter = [
      ...filter.advancedFilter,
      ...(filterEvent?.advancedFilter ?? []),
    ];

    const { pageIndex, pageSize } = pageEvent || {};
    this.setLoading(true);
    this.guidesSubscription = this.terminalsService
      .getGuidesList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filter
      )
      .pipe(
        map((result) => {
          this.totalGuides$.next(result.size);
          return result.items;
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("Guide.guidesLoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false))
      )
      .subscribe((result: any[]) => (this.guidesList = result));
  }

  getTableConfig() {
    this.setLoading(true);
    return this.tableConfigService
      .getConfigSettings(USAGE_CODE, ConfigSettingType.TABLE_CONFIG)
      .pipe(
        map(
          (response) =>
            <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.LoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false))
      );
  }

  onSelectedRow(event) {
    this.selectedRow = event;
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.setLoading(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveSuccess"),
            type: SnackbarType.SUCCESS,
          })
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveError"),
            type: SnackbarType.SUCCESS,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false))
      )
      .subscribe();
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.loadTableData(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onConfirmClick() {
    this.dialogRef.close(this.selectedRow);
  }

  isSelectedRowEmpty(): boolean {
    return this.selectedRow.length == 0;
  }

  private setLoading(loading: boolean) {
    this.loadingCounter += loading ? 1 : -1;
    this.isLoading$.next(this.loadingCounter > 0);
  }

  ngOnDestroy(): void {
    if (this.guidesSubscription) {
      this.guidesSubscription.unsubscribe();
    }
  }
}
